import React, { useEffect, useRef } from "react";
import L, { Map as LeafletMap, Polyline as LeafletPolyline, Marker as LeafletMarker } from "leaflet";
import "leaflet/dist/leaflet.css";

export interface TripPathMapProps {
  /** Array of [lat, lng] coordinates forming the trip path */
  coords: Array<[number, number]>;
  /** Height of the map container (default 320) */
  height?: string | number;
  /** Optional Leaflet zoom to start with (default 14) */
  initialZoom?: number;
  /** Optional custom end icon (taxi for ongoing) */
  endIconUrl?: string;
  endIconSize?: [number, number];
}

/**
 * Lightweight, self-contained Leaflet map to render a trip path with start/end markers.
 * Mirrors the implementation used in the driver dashboard (frontend) for consistency.
 */
const TripPathMap: React.FC<TripPathMapProps> = ({ coords, height = 320, initialZoom = 14, endIconUrl, endIconSize }) => {
  const mapRef = useRef<LeafletMap | null>(null);
  const pathRef = useRef<LeafletPolyline | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const startMarkerRef = useRef<LeafletMarker | null>(null);
  const endMarkerRef = useRef<LeafletMarker | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // If no coordinates, clean up any existing map.
    if (!coords || coords.length === 0) {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        pathRef.current = null;
      }
      return;
    }

  const start = coords[0];
  const end = coords[coords.length - 1];

    if (!mapRef.current) {
      const map = L.map(containerRef.current, {
        zoomAnimation: false,
        fadeAnimation: false,
        inertia: false,
      }).setView(start, initialZoom);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(map);

      const poly = L.polyline(coords, { color: "#2563eb", weight: 4 }).addTo(map);
      map.fitBounds(poly.getBounds(), { padding: [20, 20] });

      // Start and end markers using SVG assets in public/markers
      const startIcon = new L.Icon({
        iconUrl: "/markers/marker-green.svg",
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -28],
      });
      const endIcon = new L.Icon({
        iconUrl: endIconUrl || "/markers/marker-red.svg",
        iconSize: endIconSize || [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -28],
      });
      startMarkerRef.current = L.marker(start, { icon: startIcon }).addTo(map);
      endMarkerRef.current = L.marker(end, { icon: endIcon }).addTo(map);

      mapRef.current = map;
      pathRef.current = poly;
    } else {
      // Update existing path and bounds
      try {
        if (pathRef.current) {
          pathRef.current.setLatLngs(coords as any);
        } else if (mapRef.current) {
          pathRef.current = L.polyline(coords, { color: "#2563eb", weight: 4 }).addTo(mapRef.current);
        }
        if (mapRef.current && pathRef.current && containerRef.current?.isConnected) {
          mapRef.current.fitBounds(pathRef.current.getBounds(), { padding: [20, 20] });
        }
        // Update markers
        if (startMarkerRef.current) startMarkerRef.current.setLatLng(start as any);
        if (endMarkerRef.current) {
          endMarkerRef.current.setLatLng(end as any);
          // also update icon if props changed
          const endIconDyn = new L.Icon({
            iconUrl: endIconUrl || "/markers/marker-red.svg",
            iconSize: endIconSize || [32, 32],
            iconAnchor: [16, 32],
            popupAnchor: [0, -28],
          });
          endMarkerRef.current.setIcon(endIconDyn);
        } else if (mapRef.current) {
          const endIconDyn = new L.Icon({
            iconUrl: endIconUrl || "/markers/marker-red.svg",
            iconSize: endIconSize || [32, 32],
            iconAnchor: [16, 32],
            popupAnchor: [0, -28],
          });
          endMarkerRef.current = L.marker(end, { icon: endIconDyn }).addTo(mapRef.current);
        }
      } catch {
        // ignore during transitions
      }
    }
  }, [coords, initialZoom, endIconUrl, endIconSize]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        try {
          // stop animations/listeners before removing
          // @ts-ignore private methods exist on Leaflet map
          mapRef.current.stop?.();
          mapRef.current.off();
          mapRef.current.remove();
        } catch {
          // ignore
        } finally {
          mapRef.current = null;
          pathRef.current = null;
          startMarkerRef.current = null;
          endMarkerRef.current = null;
        }
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        height,
        width: "100%",
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid #e2e8f0",
        background: coords.length === 0 ? "#f8fafc" : undefined,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        color: "#64748b",
      }}
    >
      {coords.length === 0 && "No path data"}
    </div>
  );
};

export default TripPathMap;